import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { Check, Loader2 } from 'lucide-react'

type Status = 'idle' | 'submitting' | 'success' | 'error'

const initialForm = { name: '', email: '', subject: '', message: '' }

export const ContactForm: FC = (): ReactNode => {
    const [form, setForm] = useState(initialForm)
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')

    const update = (key: keyof typeof form) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setForm((prev) => ({ ...prev, [key]: e.target.value }))

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setStatus('submitting')
        setError('')
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })
            const data = await res.json()
            if (!res.ok) {
                throw new Error(data.error ?? 'Something went wrong.')
            }
            setStatus('success')
            setForm(initialForm)
        } catch (err) {
            setStatus('error')
            setError(err instanceof Error ? err.message : 'Something went wrong.')
        }
    }

    if (status === 'success') {
        return (
            <div className='flex flex-col items-center justify-center rounded-3xl border border-white/10 bg-[#101013] px-8 py-16 text-center'>
                <span className='flex h-14 w-14 items-center justify-center rounded-full bg-[#168F4E]/20 text-[#3ddc84]'>
                    <Check className='h-7 w-7' />
                </span>
                <h3 className='mt-6 font-display text-2xl font-medium text-white'>
                    Message sent
                </h3>
                <p className='mt-3 max-w-sm text-sm leading-relaxed text-white/60'>
                    Thanks for reaching out. Our team will get back to you within one
                    or two business days.
                </p>
                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-8 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/5'
                >
                    Send another
                </button>
            </div>
        )
    }

    const inputClass =
        'w-full rounded-xl border border-white/10 bg-[#0e0e11] px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-colors focus:border-[#E94E1B]/70'

    return (
        <form
            onSubmit={onSubmit}
            className='rounded-3xl border border-white/10 bg-[#101013] p-6 sm:p-8'
        >
            <div className='grid gap-5 sm:grid-cols-2'>
                <div className='flex flex-col gap-2'>
                    <label className='text-xs font-semibold tracking-[0.12em] text-white/60'>
                        NAME
                    </label>
                    <input
                        required
                        value={form.name}
                        onChange={update('name')}
                        placeholder='Jordan Pace'
                        className={inputClass}
                    />
                </div>
                <div className='flex flex-col gap-2'>
                    <label className='text-xs font-semibold tracking-[0.12em] text-white/60'>
                        EMAIL
                    </label>
                    <input
                        required
                        type='email'
                        value={form.email}
                        onChange={update('email')}
                        placeholder='you@email.com'
                        className={inputClass}
                    />
                </div>
            </div>

            <div className='mt-5 flex flex-col gap-2'>
                <label className='text-xs font-semibold tracking-[0.12em] text-white/60'>
                    SUBJECT
                </label>
                <input
                    value={form.subject}
                    onChange={update('subject')}
                    placeholder='How can we help?'
                    className={inputClass}
                />
            </div>

            <div className='mt-5 flex flex-col gap-2'>
                <label className='text-xs font-semibold tracking-[0.12em] text-white/60'>
                    MESSAGE
                </label>
                <textarea
                    required
                    value={form.message}
                    onChange={update('message')}
                    placeholder='Tell us a little about what you need…'
                    rows={6}
                    className={`${inputClass} resize-none`}
                />
            </div>

            {status === 'error' && (
                <p className='mt-4 text-sm text-[#ff6b6b]'>{error}</p>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className='mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#E94E1B] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#ff5a26] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto'
            >
                {status === 'submitting' && (
                    <Loader2 className='h-4 w-4 animate-spin' />
                )}
                {status === 'submitting' ? 'Sending…' : 'Send message'}
            </button>
        </form>
    )
}
